import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";

export default function ArticleEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(null);
  const [markdown, setMarkdown] = useState("");
  const [images, setImages] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetch("/api/blogs/")
      .then((res) => res.json())
      .then((data) => {
        const article = data.find((a) => a.slug === slug);
        if (article) {
          setFormData(article);
        } else {
          alert("Article not found");
          navigate("/admin/article-manage");
        }
      });

    fetch(`/api/blogs/${slug}/`)
      .then((res) => res.text())
      .then(setMarkdown)
      .catch(() => setMarkdown("Could not load markdown content."));
  }, [slug, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    setImages(Array.from(e.target.files));
  };

  const handleSave = async () => {
    if (!formData?.title || !markdown) return alert("Missing title or markdown");

    setIsSaving(true);

    const blob = new Blob([markdown], { type: "text/markdown" });
    const file = new File([blob], `${slug}.md`, { type: "text/markdown" });

    const form = new FormData();
    form.append("slug", slug);
    form.append("title", formData.title);
    form.append("excerpt", formData.excerpt);
    form.append("category", formData.category);
    form.append("thumbnail", formData.thumbnail);
    form.append("heroImage", formData.heroImage);
    form.append("metaTitle", formData.metaTitle);
    form.append("metaDescription", formData.metaDescription);
    form.append("keywords", JSON.stringify(formData.keywords || []));
    form.append("markdown", file);
    images.forEach((img) => form.append("images", img));

    const token = localStorage.getItem("adminAccessToken");

    const res = await fetch(`/api/admin/edit-blog/${slug}/`, {
      method: "PUT",
      body: form,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.ok) {
      alert("✅ Article updated.");
      navigate("/admin/article-manage");
    } else {
      alert("❌ Update failed.");
    }

    setIsSaving(false);
  };

  if (!formData) return <p className="p-6">Loading article...</p>;

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <h1 className="text-3xl font-bold mb-4">Edit Article</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block font-medium">Title</label>
          <input className="w-full border p-2 mb-2" name="title" value={formData.title} onChange={handleChange} />

          <label className="block font-medium">Slug</label>
          <input className="w-full border p-2 mb-2" value={slug} disabled readOnly />

          <label className="block font-medium">Category</label>
          <select className="w-full border p-2 mb-2" name="category" value={formData.category} onChange={handleChange}>
            <option>Education</option>
            <option>News</option>
            <option>Market Analytics</option>
          </select>

          <label className="block font-medium">Excerpt</label>
          <textarea className="w-full border p-2 mb-2" name="excerpt" value={formData.excerpt} onChange={handleChange} />

          <label className="block font-medium">Keywords (comma-separated)</label>
          <input
            className="w-full border p-2 mb-2"
            value={formData.keywords?.join(", ")}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                keywords: e.target.value.split(",").map((k) => k.trim()),
              }));
            }}
          />

          <label className="block font-medium">Meta Title</label>
          <input className="w-full border p-2 mb-2" name="metaTitle" value={formData.metaTitle} onChange={handleChange} />

          <label className="block font-medium">Meta Description</label>
          <textarea className="w-full border p-2 mb-2" name="metaDescription" value={formData.metaDescription} onChange={handleChange} />

          <label className="block font-medium">Upload New Images</label>
          <input type="file" className="w-full border p-2 mb-4" multiple onChange={handleImageChange} />

          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Update Article"}
          </button>
        </div>

        <div>
          <label className="block font-medium">Markdown Content</label>
          <textarea
            className="w-full border p-2 mb-2 h-60"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
          />

          <label className="block font-medium mt-4">Live Preview</label>
          <div className="p-4 border bg-white overflow-auto prose">
            <ReactMarkdown>{markdown}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
